import { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { TableRow, TableCell } from '../ui/Table';
import { StatusChip } from './StatusChip';

const menuItemClass =
  'flex w-full px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]';

function RowActionsMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const el = triggerRef.current;
    if (!el) return undefined;

    const rect = el.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, left: Math.max(8, rect.right - 160) });

    const closeOnOutside = (e) => {
      if (!triggerRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const closeOnScroll = () => setOpen(false);

    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('scroll', closeOnScroll, true);

    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [open]);

  if (!items?.length) return null;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-0.5 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        More
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] min-w-[140px] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] py-1 shadow-lg"
            role="menu"
            style={{ top: position.top, left: position.left }}
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                className={item.className || menuItemClass}
                role="menuitem"
                title={item.title}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

/**
 * Statement-line row used by the reconciliation workbench tables.
 *
 * Responsibilities (plan §8):
 *  - Consistent row format across the Queue / Suggested / Matched / Ignored /
 *    Reconciled / All tabs.
 *  - Highlights outflows in red and inflows in green (money-sign).
 *  - Renders a checkbox for bulk selection when onToggleSelect is provided.
 *  - Queue / suggested lines: quick Match, Split, Ignore, etc. when the parent
 *    passes the corresponding callbacks.
 *  - Matched (draft) lines: Review, Confirm, and Reject when `onViewMatch`,
 *    `onConfirmMatch`, and `onRejectMatch` are provided.
 */
export function StatementLineRow({
  line,
  selected,
  onToggleSelect,
  onMatch,
  onSplit,
  onTransfer,
  onUploadReceipt,
  onLoanRepayment,
  onLoanDrawdown,
  onCreditCardSettlement,
  onDrawings,
  onIgnore,
  onUnignore,
  /** Opens draft match review (Matched tab). */
  onViewMatch,
  /** Confirms draft match without opening the review modal. */
  onConfirmMatch,
  /** Voids draft match and returns the line to the queue. */
  onRejectMatch,
  showActions = true,
}) {
  const amount = Number(line?.amount ?? 0);
  const amountDisplay = useMemo(() => {
    if (!Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount);
  }, [amount]);

  const dateDisplay = line?.transaction_date
    ? new Date(line.transaction_date).toISOString().slice(0, 10)
    : '—';

  const amountClass =
    amount > 0
      ? 'text-green-700 dark:text-green-400 font-medium'
      : amount < 0
        ? 'text-red-700 dark:text-red-400 font-medium'
        : 'text-gray-600 dark:text-gray-300';

  const status = line?.status || 'unmatched';

  const queueStatuses = ['unmatched', 'suggested', 'partially_matched', 'needs_review'];
  const isQueueStatus = queueStatuses.includes(status);

  const secondaryMenuItems = useMemo(() => {
    if (!isQueueStatus) return [];

    const items = [];

    if (onSplit) {
      items.push({ label: 'Split', onClick: () => onSplit(line) });
    }
    if (onTransfer) {
      items.push({ label: 'Transfer', onClick: () => onTransfer(line) });
    }
    if (onUploadReceipt && amount < 0) {
      items.push({
        label: 'Upload receipt…',
        onClick: () => onUploadReceipt(line),
        title: 'Upload a supplier invoice or receipt and match this outflow',
      });
    }
    if (onLoanRepayment) {
      items.push({
        label: 'Loan pmt',
        onClick: () => onLoanRepayment(line),
        title: 'Match to a loan repayment schedule line',
        className: `${menuItemClass} text-purple-700 dark:text-purple-400`,
      });
    }
    if (onLoanDrawdown) {
      items.push({
        label: 'Drawdown',
        onClick: () => onLoanDrawdown(line),
        title: 'Match this deposit as a loan drawdown',
        className: `${menuItemClass} text-purple-700 dark:text-purple-400`,
      });
    }
    if (onCreditCardSettlement) {
      items.push({
        label: 'CC settle',
        onClick: () => onCreditCardSettlement(line),
        title: 'Match this as a credit-card settlement (bank ↔ card payment)',
        className: `${menuItemClass} text-amber-700 dark:text-amber-400`,
      });
    }
    if (onDrawings) {
      items.push({
        label: 'Drawings',
        onClick: () => onDrawings(line),
        title: 'Record this as a proprietor-drawings event (transfer / personal spend / reimbursement)',
        className: `${menuItemClass} text-emerald-700 dark:text-emerald-400`,
      });
    }
    if (['unmatched', 'needs_review', 'suggested'].includes(status) && onIgnore) {
      items.push({
        label: 'Ignore',
        onClick: () => onIgnore(line),
        className: `${menuItemClass} text-gray-500 dark:text-gray-400`,
      });
    }

    return items;
  }, [
    isQueueStatus,
    status,
    line,
    onSplit,
    onTransfer,
    onUploadReceipt,
    amount,
    onLoanRepayment,
    onLoanDrawdown,
    onCreditCardSettlement,
    onDrawings,
    onIgnore,
  ]);

  return (
    <TableRow>
      {onToggleSelect && (
        <TableCell className="w-8">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onToggleSelect(line.id, e.target.checked)}
            aria-label={`Select line ${line.line_number ?? line.id}`}
          />
        </TableCell>
      )}
      <TableCell>{dateDisplay}</TableCell>
      <TableCell className="max-w-[280px] truncate" title={line?.description || ''}>
        {line?.description || '—'}
      </TableCell>
      <TableCell className="max-w-[160px] truncate" title={line?.reference || ''}>
        {line?.reference || '—'}
      </TableCell>
      <TableCell className="max-w-[200px] truncate" title={line?.counterparty || ''}>
        {line?.counterparty || '—'}
      </TableCell>
      <TableCell align="right" className={amountClass}>
        {amountDisplay}
      </TableCell>
      <TableCell>
        <StatusChip status={status} />
      </TableCell>
      {showActions && (
        <TableCell className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            {isQueueStatus && onMatch && (
              <button
                type="button"
                onClick={() => onMatch(line)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Match
              </button>
            )}
            {isQueueStatus && secondaryMenuItems.length > 0 && (
              <RowActionsMenu items={secondaryMenuItems} />
            )}
            {status === 'matched' && onViewMatch && (
              <button
                type="button"
                onClick={() => onViewMatch(line)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Review
              </button>
            )}
            {status === 'matched' && onConfirmMatch && (
              <button
                type="button"
                onClick={() => onConfirmMatch(line)}
                className="text-xs text-green-700 hover:underline dark:text-green-400"
                title="Post to the ledger and finalise this draft match"
              >
                Confirm
              </button>
            )}
            {status === 'matched' && onRejectMatch && (
              <button
                type="button"
                onClick={() => onRejectMatch(line)}
                className="text-xs text-red-600 hover:underline dark:text-red-400"
                title="Void this draft match and return the line to the queue"
              >
                Reject
              </button>
            )}
            {status === 'ignored' && onUnignore && (
              <button
                type="button"
                onClick={() => onUnignore(line)}
                className="text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                Un-ignore
              </button>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}

export default StatementLineRow;
